import * as React from 'react';
import { ReactNode, RefObject } from 'react';
import { ValidationResult } from '../../../../util/validation/interfaces';
import {
  ApiResponse,
  ExternalIdDTO,
  ReferencedPersonDTO,
  ReferencedPersonNameDTO,
  SolrDocument,
  SuggestApi,
  WorkPersonDTO,
} from '../../../../api/core';
import { Suggester } from './Suggester';
import { ExternalId } from '../../../detail/components/ExternalId';
import { renderSelectedTemplate, topOrgaIcons } from './utils';

interface Props {
  value: SelectedPerson[];
  onChange?: (values: SelectedPerson[]) => void;
  multiple: boolean;
  disabled?: boolean;
  required?: boolean;
  validation?: ValidationResult;
  inputId?: string;
  autoFocus?: boolean;
  border?: 'border-noround' | 'border-noround-right';
  tooltip?: string;
  tooltipOptions?: any;
  ref?: RefObject<any>;
}

interface SolrPerson extends SolrDocument {
  id: string;
  f_top_orga?: string[];
  person_givenname?: string;
  person_surname?: string;
  person_aka?: string[];
  f_organisation?: string[];
  f_group?: string[];
  gnd_id?: string[];
  orcid_id?: string[];
}

export interface SelectedPerson {
  id?: number;
  personName: ReferencedPersonNameDTO;
  topOrgas: string[];
  externalIds: ExternalIdDTO[];
  aka: string[];
  organisations: string[];
  projects: string[];
}

export const referencedPersonToSelectedPerson = (referencedPerson: ReferencedPersonDTO): SelectedPerson => {
  return {
    id: referencedPerson.id,
    personName: {
      ...referencedPerson.personNames[0],
      personId: referencedPerson.id,
    },
    externalIds: referencedPerson.externalIds || [],
    topOrgas: referencedPerson.topOrgas || [],
    aka: [],
    organisations: [],
    projects: [],
  };
};

export const selectedPersonToReferencedPerson = (selected: SelectedPerson): ReferencedPersonDTO => {
  return {
    id: selected.personName.personId,
    personNames: [{
      ...selected.personName,
    }],
    externalIds: selected.externalIds,
    topOrgas: selected.topOrgas,
  };
};

export const workPersonToSelectedPerson = (workPerson: WorkPersonDTO): SelectedPerson => {
  return {
    id: workPerson.id,
    personName: {...workPerson.personName},
    externalIds: workPerson.externalIds || [],
    topOrgas: workPerson.topOrgas || [],
    aka: [],
    organisations: [],
    projects: [],
  };
};

export const selectedPersonToWorkPerson = (selected: SelectedPerson): WorkPersonDTO => {
  return {
    id: selected.id,
    personName: { ...selected.personName },
    topOrgas: selected.topOrgas || [],
    externalIds: selected.externalIds || [],
    affiliation: undefined,
  };
};

export function PersonSuggester(props: Props): ReactNode {

  const solrToSelectedPerson = (solr: SolrPerson): SelectedPerson => {
    return {
      topOrgas: solr.f_top_orga || [],
      personName: {
        id: 0, // TODO check: 0 als Platzhalter da bei WorkPerson/ProjectPerson gesetzt und ansonsten irrelevant?
        givenname: solr.person_givenname || "",
        surname: solr.person_surname || "",
        personId: +solr.id,
      },
      externalIds: [
        ...(solr.gnd_id || []).map((externalId: string): ExternalIdDTO => ({externalId, type: 'gnd'})),
        ...(solr.orcid_id || []).map((externalId: string): ExternalIdDTO => ({
          externalId,
          type: 'orcid',
        })),
      ],
      aka: solr.person_aka || [],
      organisations: solr.f_organisation || [],
      projects: solr.f_group || [],
    };
  };

  const request = (params: { q: string }): Promise<ApiResponse<SolrDocument[]>> =>
    new SuggestApi().personSuggestRaw(params);

  const selectedItemTemplate = (item: SelectedPerson): ReactNode => {
    const icons: { icon: string; title: string }[] = topOrgaIcons(item.topOrgas);
    return renderSelectedTemplate(`${[item.personName.givenname, item.personName.surname].filter(Boolean).join(' ')} (${item.personName?.personId})`, icons);
  };

  const itemTemplate = (item: SelectedPerson): ReactNode => {
    const icons: { icon: string; title: string }[] = topOrgaIcons(item.topOrgas);
    return (
      <div className={'max-w-30rem flex'}>
        <div className={'w-1 flex flex-column gap-1'}>
          {icons.length > 0 &&
            icons.map(
              (icon: { icon: string; title: string }, idx: number): ReactNode => (
                <img key={idx} src={icon.icon} alt={`${icon.title} Logo Icon`} className={'max-w-2rem'} />
              )
            )}
        </div>
        <div className={'w-11 flex flex-column gap-1'}>
          <div className={'flex flex-column white-space-normal'}>
            <span>
              <strong>{[item.personName.givenname, item.personName.surname].filter(Boolean).join(' ')}</strong> (
              {item.personName.personId})
            </span>
            {item.aka && item.aka.length > 0 && <small>{item.aka.join(', ')}</small>}
          </div>
          <div className={'flex flex-column white-space-normal'}>
            {item.topOrgas && (item.topOrgas || []).join(', ')}
            {item.organisations && item.organisations.length > 0 && (
              <small>{item.organisations.map((s: string): string => s.substring(s.indexOf('#') + 1)).join(', ')}</small>
            )}
          </div>
          {item.projects && item.projects.length > 0 && (
            <small>{item.projects.map((s: string): string => s.substring(s.indexOf('#') + 1)).join(', ')}</small>
          )}
          {item.externalIds && item.externalIds.length > 0 && (
            <div className={'flex gap-1'}>
              {(item.externalIds || []).map(
                (externalId: ExternalIdDTO, idx: number): ReactNode => (
                  <div key={idx}>
                    <ExternalId id={{ ...externalId }} entity={'person'} />
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Suggester<SelectedPerson, SelectedPerson>
      autoFocus={props.autoFocus}
      itemTemplate={itemTemplate}
      selectedItemTemplate={selectedItemTemplate}
      load={request}
      map={solrToSelectedPerson}
      inputId={props.inputId}
      value={props.value}
      disabled={props.disabled}
      validation={props.validation}
      required={props.required}
      onChange={props.onChange}
      border={props.border}
      tooltip={props.tooltip}
      tooltipOptions={props.tooltipOptions}
      ref={props.ref}
      multiple={props.multiple}/>
  );
}
